// ============================================================================
// JOURNAL DEFAULTS — two preferences, both about what the composer SHOWS.
//
// SCOPE IS DELIBERATELY TINY, and the reason is a product rule rather than
// laziness: there is no server-side settings store in LifeOS and inventing one
// for two booleans would mean a table, a route, a contract and a migration for a
// preference that is already correct in the only browser the owner writes from.
// So these live exactly where the theme and the UI scale live — one namespaced
// localStorage key each, read through a module store, never sent anywhere.
//
// WHAT THEY ARE ALLOWED TO DO. Both hide a piece of CHROME on the Today
// composer. Neither touches what is stored: a hidden mood row does not erase a
// mood that is already on the entry, and hiding the prompt does not stop the
// entry from carrying one (the day page still shows it). Hidden ≠ missing is the
// invariant, and a setting that quietly deleted data would break it.
//
// Same shape as scale.ts on purpose: pure read/write functions that take their
// storage as an argument (testable without a browser), plus one store below.
// ============================================================================

import { useSyncExternalStore } from 'react'

export interface JournalPrefs {
  /** Show the day's prompt above the composer on Today. */
  dailyPrompt: boolean
  /** Show the Mood and Energy dot rows under the composer on Today. */
  moodEnergy: boolean
}

/** The localStorage keys. Namespaced like THEME_STORAGE_KEY and SCALE_STORAGE_KEY. */
export const DAILY_PROMPT_STORAGE_KEY = 'lifeos.journal.dailyPrompt'
export const MOOD_ENERGY_STORAGE_KEY = 'lifeos.journal.moodEnergy'

/**
 * Both default to SHOWN, which is what the app already does today. A default
 * that changed the composer for anyone who never opens this screen would be a
 * redesign wearing a settings screen's clothes.
 */
export const DEFAULT_JOURNAL_PREFS: JournalPrefs = { dailyPrompt: true, moodEnergy: true }

/** 'shown' | 'hidden' on the wire, not 'true'/'false': a stored value should read as itself. */
function decode(raw: string | null | undefined, fallback: boolean): boolean {
  if (raw === 'shown') return true
  if (raw === 'hidden') return false
  return fallback
}

/**
 * What the owner last chose, or the defaults. A corrupt or absent value degrades
 * to the default rather than throwing on the render path, and `localStorage`
 * itself throws in a locked-down browser profile — so the read is guarded too.
 */
export function readJournalPrefs(storage: Pick<Storage, 'getItem'> | null | undefined): JournalPrefs {
  try {
    return {
      dailyPrompt: decode(storage?.getItem(DAILY_PROMPT_STORAGE_KEY), DEFAULT_JOURNAL_PREFS.dailyPrompt),
      moodEnergy: decode(storage?.getItem(MOOD_ENERGY_STORAGE_KEY), DEFAULT_JOURNAL_PREFS.moodEnergy),
    }
  } catch {
    return DEFAULT_JOURNAL_PREFS
  }
}

/** Persist one preference. */
export function writeJournalPref(
  storage: Pick<Storage, 'setItem'> | null | undefined,
  key: keyof JournalPrefs,
  value: boolean,
): void {
  try {
    storage?.setItem(
      key === 'dailyPrompt' ? DAILY_PROMPT_STORAGE_KEY : MOOD_ENERGY_STORAGE_KEY,
      value ? 'shown' : 'hidden',
    )
  } catch {
    // A browser that refuses storage still gets a working control for this session. Losing the
    // preference on reload beats refusing to change the composer at all.
  }
}

// ── The store ───────────────────────────────────────────────────────────────
//
// Module level for the same reason useTheme.ts is: two consumers (the composer,
// which reads, and the settings screen, which writes) and two useState copies of
// one fact drift the moment either re-renders alone.

let prefs: JournalPrefs = readJournalPrefs(
  typeof window === 'undefined' ? null : window.localStorage,
)

const listeners = new Set<() => void>()

function getSnapshot(): JournalPrefs {
  return prefs
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => void listeners.delete(listener)
}

export function setJournalPref(key: keyof JournalPrefs, value: boolean): void {
  if (prefs[key] === value) return
  writeJournalPref(typeof window === 'undefined' ? null : window.localStorage, key, value)
  // A fresh object every time is required — `useSyncExternalStore` compares by identity — but
  // only when something actually changed, which the guard above enforces.
  prefs = { ...prefs, [key]: value }
  for (const listener of listeners) listener()
}

/** Mount anywhere; every caller reads the same store. */
export function useJournalPrefs(): JournalPrefs {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
